import { useTranslation } from '@instanexus/i18n';
import { Button } from '@instanexus/ui';
import type { ReactElement } from 'react';

export interface ErrorFallbackProps {
  /** The server's `x-request-id`, when the crash was (or wrapped) an `ApiError` carrying one. */
  requestId?: string;
  onReset: () => void;
}

/**
 * The whole-app crash screen. The copy lives in the shared `visitor:errorBoundary` keys — the
 * same sentence for the same failure in every console; there is no platform-specific variant.
 */
export function ErrorFallback({ requestId, onReset }: ErrorFallbackProps): ReactElement {
  const { t } = useTranslation('visitor');

  return (
    <main className="bg-surface text-primary flex min-h-svh flex-col items-center justify-center gap-6 px-6 py-10 text-center">
      <div className="flex max-w-xl flex-col items-center gap-2">
        <h1 className="text-2xl font-semibold text-primary text-balance">
          {t('errorBoundary.title')}
        </h1>
        <p className="text-base text-muted text-pretty">{t('errorBoundary.description')}</p>
      </div>
      <Button type="button" onClick={onReset}>
        {t('errorBoundary.action')}
      </Button>
      {requestId === undefined ? null : (
        <p className="text-sm text-muted">
          {t('errorBoundary.requestIdLabel')}
          {': '}
          <span className="select-all font-mono">{requestId}</span>
        </p>
      )}
    </main>
  );
}
